import {
  CCTV_UNAVAILABLE_LABEL,
  DEVICE_TYPES,
  DISCONNECTED_COLOR,
  DISCONNECTED_GLYPH,
  isDisconnected,
  isUnavailableCctv,
  markerColor,
  markerOpacity,
  markerPaint,
  type ModelDevicePoint,
} from "./deviceTypes";
import { ICON_SHADOW_COLOR } from "./deviceAppearance";
import { SEVERITY_LABELS } from "./severity";
import type { DeviceStatus } from "@safers/api";
import "./modelMarkers.css";

/**
 * 장비 핀 버튼 — 2D 지도와 3D 뷰어가 장비 자리에 똑같이 올린다.
 */
export interface DevicePin {
  button: HTMLButtonElement;
  title: HTMLSpanElement;
  place: HTMLSpanElement;
  /** 아이콘을 DOM 으로 그릴 때(2D)만 있다. 3D 는 아이콘을 WebGL 스프라이트로 그린다. */
  icon: { element: HTMLSpanElement; svg: string } | null;
}

export function createDevicePin(
  deviceId: string,
  onSelect: (id: string, button: HTMLButtonElement) => void,
  options: { pin?: boolean } = {}
): DevicePin {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "model-device-hit";
  button.dataset.modelDeviceId = deviceId;
  let icon: DevicePin["icon"] = null;
  if (options.pin) {
    const element = document.createElement("span");
    element.className = "model-device-icon";
    element.setAttribute("aria-hidden", "true");
    button.appendChild(element);
    button.classList.add("model-device-hit--pin");
    button.style.width = `${DEVICE_PIN_BOX.width}px`;
    button.style.height = `${DEVICE_PIN_BOX.height}px`;
    button.style.setProperty("--pulse-x", `${DEVICE_PIN_BOX.headX}px`);
    button.style.setProperty("--pulse-y", `${DEVICE_PIN_BOX.headY}px`);
    button.style.setProperty("--pulse-size", `${DEVICE_PIN_BOX.headSize}px`);
    icon = { element, svg: "" };
  }
  const tag = document.createElement("span");
  tag.className = "model-device-title";
  const title = document.createElement("span");
  const place = document.createElement("span");
  place.className = "model-device-title__place";
  place.hidden = true;
  tag.append(title, place);
  button.appendChild(tag);
  button.addEventListener("pointerdown", (event) => event.stopPropagation());
  button.addEventListener("click", () => onSelect(deviceId, button));
  return { button, title, place, icon };
}

export function setDevicePinPlace(pin: DevicePin, text: string | null) {
  pin.place.textContent = text ?? "";
  pin.place.hidden = !text;
}

const PIN_PATH =
  "M14.5 1C20.8513 1 26 6.22371 26 12.667C25.9999 15.9322 24.676 18.8824 22.5449 21H22.5479L14.5 31L6.50391 21.0469C4.34468 18.9261 3.00011 15.9567 3 12.667C3 6.22374 8.14872 1.00005 14.5 1Z";
const PIN_HEAD = { x: 14.5, y: 12.667, r: 11.5 };
const PIN_TIP_Y = 31;
const PIN_PAD = 3;
const PIN_VIEW = { width: 29 + PIN_PAD * 2, height: PIN_TIP_Y + PIN_PAD * 2 };
const PIN_SCALE = 1.2;

export const DEVICE_PIN_BOX = {
  width: Math.round(PIN_VIEW.width * PIN_SCALE),
  height: Math.round(PIN_VIEW.height * PIN_SCALE),
  anchorX: Math.round((PIN_HEAD.x + PIN_PAD) * PIN_SCALE),
  anchorY: Math.round((PIN_TIP_Y + PIN_PAD) * PIN_SCALE),
  headX: Math.round((PIN_HEAD.x + PIN_PAD) * PIN_SCALE),
  headY: Math.round((PIN_HEAD.y + PIN_PAD) * PIN_SCALE),
  headSize: Math.round(PIN_HEAD.r * 2 * PIN_SCALE),
} as const;

function devicePinSvg(device: ModelDevicePoint): string {
  const paint = markerPaint(device);
  const disconnected = isDisconnected(device);
  const iconScale = (PIN_HEAD.r * 1.15) / 24;
  const iconOffset = 12 * iconScale;
  return `<svg width="${DEVICE_PIN_BOX.width}" height="${DEVICE_PIN_BOX.height}" viewBox="${-PIN_PAD} ${-PIN_PAD} ${PIN_VIEW.width} ${PIN_VIEW.height}" opacity="${markerOpacity(device)}" aria-hidden="true">
    <path d="${PIN_PATH}" fill="${paint.fill}" stroke="${paint.ring}" stroke-width="${disconnected ? 2.2 : 1.5}" stroke-linejoin="round" style="filter:drop-shadow(0 1px 1.5px ${ICON_SHADOW_COLOR}80)"/>
    <path d="${DEVICE_TYPES[device.type].path}" transform="translate(${PIN_HEAD.x - iconOffset} ${PIN_HEAD.y - iconOffset}) scale(${iconScale})" fill="none" stroke="${paint.icon}" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
    ${disconnected ? disconnectedBadgeSvg(PIN_HEAD.x + PIN_HEAD.r * 0.72, PIN_HEAD.y - PIN_HEAD.r * 0.72, 6.4) : ""}
  </svg>`;
}

export function disconnectedBadgeSvg(cx: number, cy: number, r: number) {
  const scale = (r * 1.5) / 24;
  return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${DISCONNECTED_COLOR}" stroke="white" stroke-width="${r * 0.22}"/>
    <path d="${DISCONNECTED_GLYPH}" transform="translate(${cx - 12 * scale} ${cy - 12 * scale}) scale(${scale})" fill="none" stroke="white" stroke-width="3" stroke-linecap="round"/>`;
}

const STATUS_ARIA_TEXT: Record<DeviceStatus, string | null> = {
  ACTIVE: null,
  DELAYED: "통신 지연",
  OFFLINE: "통신 끊김",
};

/** 핀을 다시 만들지 않고 장비 상태에 맞춘다 — 팝업이 붙은 버튼이 그대로 남는다. */
export function updateDevicePin(pin: DevicePin, device: ModelDevicePoint) {
  const { button, title, icon } = pin;
  title.textContent = device.name || device.deviceId;
  if (icon) {
    const svg = devicePinSvg(device);
    if (icon.svg !== svg) {
      icon.element.innerHTML = svg;
      icon.svg = svg;
    }
  }
  button.setAttribute(
    "aria-label",
    [
      device.name || device.deviceId,
      !device.statusLabel && device.status && STATUS_ARIA_TEXT[device.status],
      device.statusLabel ?? SEVERITY_LABELS[device.severity],
    ]
      .filter(Boolean)
      .join(" · ")
  );
  button.style.outline = device.selected ? "3px solid #ff7500" : "";
  button.classList.toggle("is-alert", !!device.alert);
  button.dataset.severity = device.severity;
  button.style.setProperty("--pulse-color", markerColor(device));
  if (isUnavailableCctv(device)) {
    button.setAttribute("aria-disabled", "true");
    button.title = CCTV_UNAVAILABLE_LABEL;
  } else {
    button.removeAttribute("aria-disabled");
    button.removeAttribute("title");
  }
  if (device.selected != null) {
    button.setAttribute("aria-pressed", String(device.selected));
    button.removeAttribute("aria-haspopup");
  } else {
    button.removeAttribute("aria-pressed");
    if (device.type !== "CCTV") button.setAttribute("aria-haspopup", "dialog");
    else button.removeAttribute("aria-haspopup");
  }
}
